import { Box, Chip, CircularProgress, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { dateFormat } from '../../helpers/dateFormat';
import { formatCurrency } from '../../helpers/formatCurrency';
import { ICommissionAppointmentItem, ICommissionCheckpointView, ICommissionGoalProgress } from '../../services';

interface CommissionEmployeeExpandableProps {
  expanded: boolean;
  items: ICommissionAppointmentItem[];
  totalGrossRevenue: number;
  totalCommission: number;
  checkpoints: ICommissionCheckpointView[] | undefined;
  loadingCheckpoints: boolean;
  goalProgress: ICommissionGoalProgress[] | undefined;
  payoutHistory?: IPayoutHistoryItem[];
}

export interface IPayoutHistoryItem {
  id: number;
  amount: number;
  status: number;
  statusName: string;
  requestedAt: Date;
  paidAt?: Date;
  notes?: string;
}

const PaidChip: React.FC<{ paidAmount: number; commission: number }> = ({ paidAmount, commission }) => {
  if (paidAmount <= 0) return null;
  const isFullyPaid = paidAmount >= commission;
  return (
    <Chip
      label={isFullyPaid ? 'Pago' : 'Parcial'}
      size='small'
      color={isFullyPaid ? 'success' : 'warning'}
      variant='outlined'
      sx={{ height: 18, '& .MuiChip-label': { fontSize: 10, px: 0.5 }, ml: 0.5 }}
    />
  );
};

export const CommissionEmployeeExpandable: React.FC<CommissionEmployeeExpandableProps> = ({
  expanded,
  items,
  totalGrossRevenue,
  totalCommission,
  checkpoints,
  loadingCheckpoints,
  goalProgress,
  payoutHistory,
}) => {
  // Calcula totais de pagamento a partir dos items
  const totalPaid = items.reduce((s, a) => s + (a.paidAmount ?? 0), 0);
  const totalPending = totalCommission - totalPaid;

  return (
    <Collapse in={expanded} timeout='auto' unmountOnExit>
      <Box sx={{ py: 2, px: 1 }}>

        {/* Checkpoints */}
        {checkpoints && checkpoints.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
              Últimos fechamentos de ciclo
            </Typography>
            <Table size='small' sx={{ minWidth: 400, mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Ciclo</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell align='right'>Comissão</TableCell>
                  <TableCell align='center'>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkpoints.map((cp) => (
                  <TableRow key={cp.id}>
                    <TableCell>#{cp.cycleIndex}</TableCell>
                    <TableCell>
                      {new Date(cp.periodStart).toLocaleDateString('pt-BR')} - {new Date(cp.periodEnd).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>
                      R$ {cp.totalCommission.toFixed(2)}
                    </TableCell>
                    <TableCell align='center'>
                      <Chip
                        label={cp.isCurrentCycle ? 'Em andamento' : 'Fechado'}
                        size='small'
                        color={cp.isCurrentCycle ? 'info' : 'success'}
                        variant='outlined'
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {loadingCheckpoints && (
          <Box display='flex' justifyContent='center' py={1}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Goal progress */}
        {goalProgress && goalProgress.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
              Progresso das Metas
            </Typography>
            <Box display='flex' flexDirection='column' gap={1}>
              {goalProgress.map((gp) => (
                <Box key={gp.goalId} display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                  <Typography variant='body2' fontWeight={500}>
                    {gp.goalName}
                  </Typography>
                  <Chip
                    label={`${gp.currentProgress}${gp.metric === 'Valor' ? '' : ''} → ${gp.currentTierLabel}`}
                    size='small'
                    color='primary'
                    variant='outlined'
                  />
                  {gp.nextTierLabel && (
                    <Typography variant='caption' color='text.secondary'>
                      +{gp.metric === 'Quantidade' ? gp.progressToNext : formatCurrency(Math.max(0, gp.progressToNext))} para {gp.nextTierLabel}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Appointments detail */}
        {items && items.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Itens</TableCell>
                  <TableCell align='right'>Total</TableCell>
                  <TableCell align='right'>Pagamento</TableCell>
                  <TableCell align='right'>Comissão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((appt) => (
                  <TableRow key={appt.appointmentId}>
                    <TableCell>{dateFormat(appt.date, 'DD/MM [ - ] HH[h]mm')}</TableCell>
                    <TableCell>{appt.customerName}</TableCell>
                    <TableCell>
                      {appt.itemDetails?.map((d, i) => (
                        <Box key={i} display='flex' gap={1} alignItems='center' flexWrap='wrap'>
                          <Typography variant='body2'>
                            {d.name}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatCurrency(d.amount)}
                          </Typography>
                          <Chip
                            label={`+${formatCurrency(d.commission)}`}
                            size='small'
                            color='success'
                            variant='outlined'
                            sx={{ height: 18, '& .MuiChip-label': { fontSize: 11, px: 0.5 } }}
                          />
                          {/* Paid chip por item */}
                          <PaidChip paidAmount={d.paidAmount} commission={d.commission} />
                          {/* Meta info: se tem goal, mostra qual + ordinal */}
                          {d.goalName && (
                            <Chip
                              label={`#${d.sequenceNumber} ${d.goalName}${d.tierIndex != null ? ` T${d.tierIndex + 1}` : ''}`}
                              size='small'
                              color='info'
                              variant='outlined'
                              sx={{ height: 18, '& .MuiChip-label': { fontSize: 10, px: 0.5 } }}
                            />
                          )}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell align='right'>{formatCurrency(appt.totalAmount)}</TableCell>
                    <TableCell align='right'>
                      <Chip label={appt.paymentMethodName} size='small' variant='outlined' />
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>
                      <Box display='flex' alignItems='center' justifyContent='flex-end'>
                        {formatCurrency(appt.commissionAmount)}
                        <PaidChip paidAmount={appt.paidAmount} commission={appt.commissionAmount} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2}><strong>Total</strong></TableCell>
                  <TableCell />
                  <TableCell align='right'><strong>{formatCurrency(totalGrossRevenue)}</strong></TableCell>
                  <TableCell />
                  <TableCell align='right' sx={{ fontWeight: 700, color: 'primary.main' }}>
                    <Box display='flex' alignItems='center' justifyContent='flex-end' gap={1}>
                      <strong>{formatCurrency(totalCommission)}</strong>
                      {totalPaid > 0 && (
                        <Typography variant='caption' color='text.secondary'>
                          ({formatCurrency(totalPending)} pend.)
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' textAlign='center' py={2}>
            Nenhum detalhamento disponível para o período.
          </Typography>
        )}

        {/* ── Payout history ── */}
        {payoutHistory && payoutHistory.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>Histórico de Pagamentos</Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align='right'>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Observações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutHistory.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{dateFormat(p.paidAt ?? p.requestedAt, 'DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell align='right'>{formatCurrency(p.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.statusName}
                        size='small'
                        color={p.status === 1 ? 'success' : p.status === 0 ? 'warning' : 'error'}
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color='text.secondary'>
                        {p.notes || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Collapse>
  );
};
